import { KohostSocketIoClient as SocketIoClient } from "@kohost/api-client/socketIoClient";
import { RefreshTokenCommand } from "@kohost/api-client/useCases";
import kohost from "lib/api/client";
import { getApiUrl } from "lib/api/endpoints";
import React, { createContext, useContext, useEffect } from "react";
import { captureException } from "services/sentry";
import { demoOrgs } from "../api/organization";
import { useAuth } from "./use-auth";
import { useOrganization } from "./use-organization";
import { useCurrentProperty } from "./use-properties";

const apiUrl = getApiUrl();

const socketContext = createContext();

export function ProvideSocketIo({ children }) {
  const { connected, client } = useProvideSocketIo();

  return (
    <socketContext.Provider
      value={{
        client: client,
        connected,
      }}
    >
      {children}
    </socketContext.Provider>
  );
}

export const useSocketIo = () => {
  return useContext(socketContext);
};

function getSocketUrl(propertyId) {
  let url = apiUrl;
  if (apiUrl.includes("/api/")) {
    url = apiUrl.replace("/api/", `/${propertyId}`);
  } else {
    url = apiUrl + `${propertyId}`;
  }
  return url;
}

export const useProvideSocketIo = () => {
  const { currentUser } = useAuth();
  const { organization } = useOrganization();
  const { id: currentPropertyId } = useCurrentProperty();

  const currentUserId = currentUser?.id;

  const socketClientRef = React.useRef(
    new SocketIoClient({
      url: getSocketUrl(currentPropertyId),
      options: {
        extraHeaders: {
          "X-Kohost-Api-Version": "v3legacy",
        },
      },
    }),
  );

  const socketClient = socketClientRef.current;
  const connected = socketClient.connected || socketClient.socket?.connected;

  const timeout = React.useRef(null);

  const isDemo = demoOrgs.includes(organization?.id);

  const failCount = React.useRef(0);

  useEffect(() => {
    let ignore = false;

    if (
      !ignore &&
      currentUserId &&
      !connected &&
      currentPropertyId &&
      !isDemo
    ) {
      // only connect if we are logged in and not already connected
      socketClient.connect();
    }

    if (!ignore && socketClient.socket?.nsp !== `/${currentPropertyId}`) {
      socketClient.destroy();
      socketClient.current = null;
      socketClientRef.current = new SocketIoClient({
        url: getSocketUrl(currentPropertyId),
      });
    }

    const reconnectOnWindowFocus = () => {
      if (currentPropertyId && !isDemo) {
        socketClient.connect();
      }
    };

    window.addEventListener("focus", reconnectOnWindowFocus);

    const handleConnect = () => {
      console.log("Kohost Socket.io client connected");
      failCount.current = 0;
    };

    const handleDisconnect = (reason) => {
      console.log("Kohost Socket.io client disconnected:", reason);
    };

    const handleConnectError = (err) => {
      console.error("Kohost Socket.io client connection error", err);

      if (err.message === "The token has expired.") {
        try {
          console.log(
            "Kohost Socket.io client token expired, attempting to refresh",
          );
          //attempt to refresh token
          failCount.current++;
          const cmd = new RefreshTokenCommand();
          console.log("Kohost Socket.io client token refresh request");
          kohost
            .send(cmd)
            .then(() => {
              console.log(
                "Kohost Socket.io client token refreshed, reconnecting",
              );
              socketClient.connect();
            })
            .catch((err) => {
              console.error("Kohost Socket.io client token refresh error", err);
              failCount.current = 0;
              captureException(err);
            });
        } catch (err) {
          console.error("Kohost Socket.io client token refresh error", err);
          captureException(err);
        }
      } else {
        if (timeout.current) clearTimeout(timeout.current);
        timeout.current = setTimeout(() => socketClient.connect(), 1000);
        captureException(err);
      }
    };

    const hasConnectLstnr = socketClient.listeners("connect").length > 0;
    const hasDisconnectLstnr = socketClient.listeners("disconnect").length > 0;
    const hasConnectErrorLstnr =
      socketClient.listeners("connect_error").length > 0;

    if (!hasConnectLstnr) socketClient.on("connect", handleConnect);
    if (!hasDisconnectLstnr) socketClient.on("disconnect", handleDisconnect);
    if (!hasConnectErrorLstnr)
      socketClient.on("connect_error", handleConnectError);

    return () => {
      ignore = true;
      clearTimeout(timeout.current);
      window.removeEventListener("focus", reconnectOnWindowFocus);
      socketClient.disconnect();
    };
  }, [currentUserId, socketClient, currentPropertyId]);

  return { connected, client: socketClient || {} };
};
